'use client';

import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

/**
 * Full-bleed navy section: the two ways teams use Chat.co.
 * Left rail = customer-facing website agent, right rail = internal expert agent.
 * Both product mockups are hand-built in CSS — no images, no video.
 */

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E\")";

const WebsiteAgentMock = () => (
  <div className="relative mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#0d2c4d] shadow-2xl shadow-black/40">
    {/* Browser chrome */}
    <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="ml-3 flex-1 rounded-md bg-white/5 px-3 py-1 text-[10px] tracking-wide text-blue-200/50">
        yourcompany.com
      </span>
    </div>
    {/* Page skeleton */}
    <div className="relative h-[240px] px-5 pt-5">
      <div className="h-3 w-2/5 rounded bg-white/10" />
      <div className="mt-3 h-2 w-4/5 rounded bg-white/[0.06]" />
      <div className="mt-2 h-2 w-3/5 rounded bg-white/[0.06]" />
      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="h-14 rounded-lg bg-white/[0.05]" />
        <div className="h-14 rounded-lg bg-white/[0.05]" />
        <div className="h-14 rounded-lg bg-white/[0.05]" />
      </div>

      {/* Floating chat widget */}
      <div className="absolute bottom-4 right-4 w-[220px] rounded-2xl bg-white p-3.5 shadow-xl shadow-black/50">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-600">
            <Icon icon="tabler:message-chatbot" className="text-base text-white" />
          </span>
          <div>
            <div className="text-[11px] font-bold leading-tight text-gray-900">Sales Assistant</div>
            <div className="flex items-center gap-1 text-[9px] text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online 24/7
            </div>
          </div>
        </div>
        <div className="mt-2.5 rounded-xl rounded-tl-sm bg-gray-100 px-3 py-2 text-[10px] leading-snug text-gray-700">
          Hi! Ask me anything about pricing, shipping, or your order.
        </div>
        <div className="mt-2 flex items-center justify-between rounded-full border border-gray-200 px-3 py-1.5">
          <span className="text-[10px] text-gray-400">Type a question…</span>
          <Icon icon="tabler:send" className="text-xs text-primary-600" />
        </div>
      </div>
    </div>
  </div>
);

const ExpertAgentMock = () => (
  <div className="relative mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#0d2c4d] shadow-2xl shadow-black/40">
    <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-200/60">
        <Icon icon="tabler:lock" className="text-sm" /> Internal · Team workspace
      </div>
      <div className="flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-[9px] text-blue-200/60">
        <Icon icon="logos:google-drive" className="text-[11px]" />
        <Icon icon="logos:microsoft-onedrive" className="text-[11px]" />
        Synced 2 min ago
      </div>
    </div>
    <div className="h-[240px] space-y-3 px-5 py-4">
      {/* Question */}
      <div className="ml-auto w-fit max-w-[75%] rounded-2xl rounded-br-sm bg-primary-600 px-3.5 py-2 text-[11px] leading-snug text-white">
        What&apos;s our refund policy for annual contracts?
      </div>
      {/* Answer with citations */}
      <div className="w-fit max-w-[88%] rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 text-[11px] leading-relaxed text-gray-800 shadow-lg">
        Annual contracts can be refunded pro-rata within the first 60 days
        <sup className="font-bold text-primary-600"> 1</sup>. After 60 days, unused months convert
        to service credit
        <sup className="font-bold text-primary-600"> 2</sup>.
        <div className="mt-2 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-primary-600">
            <Icon icon="tabler:file-type-pdf" className="text-[10px]" /> 1 · Refund-Policy-2026.pdf
          </span>
          <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-primary-600">
            <Icon icon="tabler:file-text" className="text-[10px]" /> 2 · MSA §4.2
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 pl-1 text-[9px] text-blue-200/50">
        <Icon icon="tabler:circle-check" className="text-[11px] text-emerald-400" />
        Answered from 2 verified sources
      </div>
    </div>
  </div>
);

const AgentShowcase: React.FC = () => {
  return (
    <section
      aria-label="Two ways to use Chat.co"
      className="relative overflow-hidden bg-[#04182e] py-20 sm:py-24">
      {/* Atmosphere: faint grid + orbs + grain, echoing the hero */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" />
        <div className="absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-primary-600/25 blur-[120px]" />
        <div className="absolute -bottom-24 -right-24 h-[360px] w-[360px] rounded-full bg-[#d3a641]/10 blur-[100px]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: GRAIN }} />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Editorial header — left-aligned, kicker rule */}
        <div className="max-w-2xl">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-[#d3a641]/70" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#ddb85a]/90">
              One platform, two superpowers
            </span>
          </div>
          <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-white sm:text-[2.6rem] sm:leading-[1.15]">
            An agent for your customers.{' '}
            <span className="font-display font-medium italic text-[#e2c06a]">
              An expert for your team.
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-blue-100/70">
            Start with the easy win — a friendly agent on your website answering visitors day and
            night. Then point the same platform inward and give your whole team an expert that knows
            every document you have.
          </p>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-8">
          {/* Customer-facing */}
          <div className="group relative">
            <div className="flex items-center gap-3">
              <span className="font-display text-5xl font-light italic leading-none text-white/20">
                a
              </span>
              <div>
                <h3 className="text-xl font-bold text-white">On your website, today</h3>
                <p className="text-sm text-blue-200/60">Customer-facing · no engineers needed</p>
              </div>
            </div>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-blue-100/70">
              Paste one line of code and a branded agent greets your visitors — answering questions,
              capturing leads, and handing off to a human when it matters. It speaks your
              customer&apos;s language, literally, in any of them.
            </p>
            <ul className="mt-5 space-y-2">
              {[
                'Live 24/7 — answers while you sleep',
                'Captures name, email & phone as leads',
                'Also meets customers on WhatsApp, Slack & Teams',
              ].map((li) => (
                <li key={li} className="flex items-start gap-2.5 text-sm text-blue-100/80">
                  <Icon icon="tabler:arrow-right" className="mt-0.5 text-base text-[#d3a641]" />
                  {li}
                </li>
              ))}
            </ul>
            <WebsiteAgentMock />
          </div>

          {/* Internal expert */}
          <div className="group relative">
            <div className="flex items-center gap-3">
              <span className="font-display text-5xl font-light italic leading-none text-white/20">
                b
              </span>
              <div>
                <h3 className="text-xl font-bold text-white">Inside your company</h3>
                <p className="text-sm text-blue-200/60">
                  Internal expert · cited, verified answers
                </p>
              </div>
            </div>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-blue-100/70">
              Connect Google Drive, OneDrive, SharePoint or Notion and your documents stay in sync
              automatically. Every answer arrives with citations to the exact source — so your team
              acts on facts, not folklore.
            </p>
            <ul className="mt-5 space-y-2">
              {[
                'Syncs with the file tools you already use',
                'Every answer cites its source document',
                'Access-restricted — invite only who you choose',
              ].map((li) => (
                <li key={li} className="flex items-start gap-2.5 text-sm text-blue-100/80">
                  <Icon icon="tabler:arrow-right" className="mt-0.5 text-base text-[#d3a641]" />
                  {li}
                </li>
              ))}
            </ul>
            <ExpertAgentMock />
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Link
            href={PageLink.login}
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-[#04182e] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#d3a641]/25">
            Start 14-Day Trial
            <Icon icon="tabler:arrow-right" className="text-base" />
          </Link>
          <span className="text-sm text-blue-200/50">
            14-day trial · no credit card · paid plan after trial
          </span>
        </div>
      </div>
    </section>
  );
};

export default AgentShowcase;
